import * as React from 'react';
import { createContext, useContext } from 'react';
import type { ReactElement } from 'react';

import { ACTION_TYPES } from 'bundles/expression-of-interest/constants';
import type { EoiButtonType } from 'bundles/expression-of-interest/types';
import type { ProductTypes } from 'bundles/university-program-qualification/utils/constants';

export type ProductType = (typeof ProductTypes)[keyof typeof ProductTypes];

type EoiOptimizationsProps = {
  slug: string;
  productType?: ProductType;
  applyButtonData?: EoiButtonType | null;
};

const EoiOptimizationsContext = createContext<EoiOptimizationsProps>({
  slug: '',
  productType: undefined,
  applyButtonData: { actionType: ACTION_TYPES.apply },
});

export const useEoiOptimizationsContext = () => useContext(EoiOptimizationsContext);

type PropsToComponent = {
  children: ReactElement;
  slug: string;
  productType?: ProductType;
  applyButtonData?: EoiButtonType | null;
};

/**
 * This component provides the EOI context to its children.
 *
 * @param {React.ReactElement} props.children - The child components to be rendered within the provider.
 * @param {string} props.slug - The slug for the program.
 * @param {ProductType} [props.productType] - The type of product.
 * @param {EoiButtonType|null} [props.applyButtonData] - Data for the apply button.
 * @returns {React.ReactElement} The rendered EoiOptimizationsContextProvider component.
 */
export const EoiOptimizationsContextProvider = (props: PropsToComponent) => {
  const { children, slug, productType, applyButtonData } = props;

  return (
    <EoiOptimizationsContext.Provider
      value={{
        slug,
        productType,
        applyButtonData,
      }}
    >
      {children}
    </EoiOptimizationsContext.Provider>
  );
};
